import { useCallback, useState } from "react";

export type PlanName = "Sanctum" | "North Star";
export type BillingCycle = "Monthly" | "Annual";

export type UpgradeNowConfirmPayload = {
    plan: PlanName;
    billingCycle: BillingCycle;
};

export type UpgradeNowLogicProps = {
    onConfirm: (payload: UpgradeNowConfirmPayload) => void;
    /** Optional: surface actual trial end date in the header copy */
    trialEndsOn?: string | undefined;
    /** Optional: highlight which plan is currently active for the account */
    currentPlan?: PlanName | undefined;
};

import { PLANS } from "../../billing.constants";

/** ---- Dynamic pricing config from centralized constants ---- */
type PlanKey = "Sanctum" | "NorthStar";

const PRICING: Record<
    PlanKey,
    { monthly: number; annualTotal: number; annualSavingOverride?: number }
> = {
    Sanctum: {
        monthly: PLANS.sanctum.pricing.monthly,
        annualTotal: PLANS.sanctum.pricing.annualTotal,
        annualSavingOverride: PLANS.sanctum.pricing.annualSavings,
    },
    NorthStar: {
        monthly: PLANS.northStar.pricing.monthly,
        annualTotal: PLANS.northStar.pricing.annualTotal,
        annualSavingOverride: PLANS.northStar.pricing.annualSavings,
    },
};

const toPlanKey = (name: PlanName): PlanKey =>
    name === "North Star" ? "NorthStar" : "Sanctum";

export function fmtCurrency(n: number) {
    return n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    });
}

export function fmtTrialDate(iso?: string) {
    if (!iso) return "Unknown";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "Unknown";
    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
}

export function useUpgradeNow({
    onConfirm,
    trialEndsOn,
    currentPlan,
}: UpgradeNowLogicProps) {
    // Independent state for plan vs billing cycle
    const [selectedPlan, setSelectedPlan] = useState<PlanName>("North Star");
    const [billingCycle, setBillingCycle] = useState<BillingCycle>("Annual");

    const confirm = useCallback(() => {
        onConfirm({ plan: selectedPlan, billingCycle });
    }, [onConfirm, selectedPlan, billingCycle]);

    // Dynamic pricing strings (derived each render; no extra imports)
    const sanctumPricing = PRICING["Sanctum"];
    const sanctumSaving = sanctumPricing.annualSavingOverride ??
        sanctumPricing.monthly * 12 - sanctumPricing.annualTotal;

    const northStarPricing = PRICING["NorthStar"];
    const northStarSaving = northStarPricing.annualSavingOverride ??
        northStarPricing.monthly * 12 - northStarPricing.annualTotal;

    const trialDateText = fmtTrialDate(trialEndsOn);

    // Dynamic billing-cycle figures based on selected plan
    const selectedPricing = PRICING[toPlanKey(selectedPlan)];
    const annualMonthlyAfterDiscount = selectedPricing.annualTotal / 12;
    const annualTotal = selectedPricing.annualTotal;
    const annualSavings = selectedPricing.monthly * 12 -
        selectedPricing.annualTotal;
    const monthlyRate = selectedPricing.monthly;

    return {
        // state
        selectedPlan,
        billingCycle,
        // setters
        setSelectedPlan,
        setBillingCycle,
        // derived pricing
        sanctumPricing,
        sanctumSaving,
        northStarPricing,
        northStarSaving,
        annualMonthlyAfterDiscount,
        annualTotal,
        annualSavings,
        monthlyRate,
        selectedPricing,
        // meta
        trialDateText,
        currentPlan,
        // features
        sanctumFeatures: [...PLANS.sanctum.features],
        northStarFeatures: [...PLANS.northStar.upsellFeatures],
        // actions
        confirm,
    };
}

export type UseUpgradeNowReturn = ReturnType<typeof useUpgradeNow>;
